import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';

import { COLORS } from '../../../configs/colors';
import { parseSLASummaryString, SLAData } from '../../molecules/SLACircleIndicator';

interface PreviewDrawerSLAProps {
  service: Record<string, unknown> | null;
}

const formatRemaining = (hours: number): string => {
  if (hours < 0) return 'Breached';
  if (hours < 1) return `${Math.round(hours * 60)}m remaining`;
  if (hours < 24) return `${Math.floor(hours)}h ${Math.round((hours % 1) * 60)}m remaining`;
  const days = Math.floor(hours / 24);
  return `${days}d ${Math.floor(hours % 24)}h remaining`;
};

const getColor = (hours: number, breached: boolean): string => {
  if (breached || hours < 0) return COLORS.SLAUrgency1;
  if (hours < 4) return COLORS.SLAUrgency1;
  if (hours < 8) return COLORS.SLAUrgency2;
  if (hours < 16) return COLORS.SLAUrgency3;
  if (hours < 24) return COLORS.SLAUrgency4;
  return COLORS.SLAUrgency5;
};

/** Parse an SLA field — handles both SLASummary objects (detail API) and strings (list API). */
const parseSLA = (value: unknown): SLAData | null => {
  if (!value) return null;
  if (typeof value === 'string') return parseSLASummaryString(value);
  if (typeof value === 'object' && 'timeoutAt' in (value as Record<string, unknown>)) {
    return value as SLAData;
  }
  return null;
};

const SLABar: React.FC<{ label: string; sla: SLAData }> = ({ label, sla }) => {
  if (!sla.timeoutAt) return null;
  const timeoutMs = new Date(sla.timeoutAt).getTime();
  const hoursRemaining = (timeoutMs - Date.now()) / (1000 * 60 * 60);
  const breached = sla.slaOutcome?.outcomeTypeCode === 'breached';
  const totalWindow = 48;
  const elapsed = totalWindow - Math.max(0, hoursRemaining);
  const progress = Math.min(100, Math.max(0, (elapsed / totalWindow) * 100));
  const color = getColor(hoursRemaining, breached);
  const remaining = breached ? 'Breached' : formatRemaining(hoursRemaining);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontSize: '0.75rem', color }}>{remaining}</Typography>
      </Box>
      <Box
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-label={`${label} progress`}
        sx={{ height: 6, borderRadius: 3, bgcolor: 'v4.filterInput', overflow: 'hidden' }}
      >
        <Box sx={{ height: '100%', width: `${progress}%`, bgcolor: color, borderRadius: 3 }} />
      </Box>
    </Box>
  );
};

export const PreviewDrawerSLA: React.FC<PreviewDrawerSLAProps> = ({ service }) => {
  const theme = useTheme();

  const slaItems = useMemo(() => {
    if (!service) return [];
    const items: Array<{ label: string; sla: SLAData }> = [];
    const client = parseSLA(service.clientSLA ?? service.clientSLASummary);
    if (client) items.push({ label: 'Client SLA', sla: client });
    const ops = parseSLA(service.opsSLA ?? service.opsSLASummary);
    if (ops) items.push({ label: 'Ops SLA', sla: ops });
    return items;
  }, [service]);

  return (
    <Box>
      <Typography variant="overline" sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: 1.2, color: 'text.secondary', mb: 1, display: 'block' }}>
        SLA
      </Typography>
      {slaItems.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>No SLA data</Typography>
      ) : (
        <Box sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${theme.palette.v4.border}` }}>
          {slaItems.map((item) => <SLABar key={item.label} label={item.label} sla={item.sla} />)}
        </Box>
      )}
    </Box>
  );
};
